import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '@/components/ui/Container';
import LanguageSelector from '@/components/ui/LanguageSelector';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}

export default function LegalPageLayout({ title, lastUpdated, children }: LegalPageLayoutProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-mk-pink-600 to-mk-pink-500 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>

        <Container className="relative z-10">
          <div className="py-4 flex items-center justify-between">
            <Link to="/" className="text-white font-bold text-xl hover:opacity-90 transition-opacity">
              MyPink<span className="text-white/80">Contact</span>
            </Link>
            <LanguageSelector variant="light" />
          </div>

          <div className="py-12 md:py-16 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {title}
            </h1>
            <p className="text-white/80 text-sm">
              {t('legal.lastUpdated')}: {lastUpdated}
            </p>
          </div>
        </Container>
      </header>

      {/* Content */}
      <main className="flex-1 py-12 md:py-16">
        <Container>
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-mk-pink-600 prose-a:no-underline hover:prose-a:underline">
              {children}
            </div>
          </div>
        </Container>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8">
        <Container>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="text-white font-bold text-xl hover:opacity-90 transition-opacity">
              MyPink<span className="text-mk-pink-400">Contact</span>
            </Link>

            <p className="text-gray-400 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>

            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.privacy')}
              </Link>
              <Link to="/legal" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.legal')}
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.links.terms')}
              </Link>
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}
